import React, { createRef, useEffect, useState } from 'react';
import './pdf-viewer.scss';
import { Document, Page, pdfjs } from 'react-pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default function PDFViewer(props) {
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1);
  const [editingPageNumber, setEditingPageNumber] = useState(1);
  const [focusPageNumber, setFocusPageNumber] = useState(1);
  const [pageRefs, setPageRefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setPageRefs([]);
  }, [props.file]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);

    setPageRefs(() =>
      Array(numPages)
        .fill()
        .map(() => createRef()),
    );

    setFocusPageNumber(1);
    setEditingPageNumber(1);
    setLoading(false);
  }

  function scrollPageIntoView(pageNumber) {
    const pageRef = pageRefs[pageNumber - 1];
    if (pageRef) {
      pageRef.current.scrollIntoView();
      setFocusPageNumber(pageNumber);
    } else {
      setEditingPageNumber(focusPageNumber);
    }
  }

  function documentHeader() {
    return (
      <div className="PDFheader">
        <div className="header-left">
          <input
            value={editingPageNumber}
            className="pageNumberInput"
            type="text"
            onChange={(e) => {
              const newValue = e.target.value;
              setEditingPageNumber(newValue);
            }}
            onBlur={() => {
              scrollPageIntoView(editingPageNumber);
            }}
          />
          <div className="pagesOf">of {numPages}</div>
        </div>
        <div className="header-right">
          <button
            className="bar-icon"
            data-element-focusable="true"
            title="Fit to width"
            onClick={() => setScale(1)}
          >
            <span className="icon-symbol">
              <svg width="24" viewBox="0 0 21 20">
                <path d="M7.38 7.92a.5.5 0 01-.04.7l-1 .88H9a.5.5 0 010 1H6.34l1 .87a.5.5 0 11-.66.76l-2-1.75a.5.5 0 010-.76l2-1.75a.5.5 0 01.7.05z"></path>
                <path d="M14.68 10.5l-1 .87a.5.5 0 00.66.76l2-1.75a.5.5 0 000-.76l-2-1.75a.5.5 0 00-.66.76l1 .87H12a.5.5 0 000 1h2.67z"></path>
                <path d="M2.5 6c0-1.1.9-2 2-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-12a2 2 0 01-2-2V6zm2-1a1 1 0 00-1 1v8a1 1 0 001 1h12a1 1 0 001-1V6a1 1 0 00-1-1h-12z"></path>
              </svg>
            </span>
          </button>
          <button
            className="bar-icon"
            data-element-focusable="true"
            title="Zoom in"
            onClick={() => setScale(Math.min(5, scale * 1.1))}
          >
            <span className="is-icon-4 icon-plus icon-symbol"></span>
          </button>
          <button
            className="bar-icon"
            data-element-focusable="true"
            title="Zoom out"
            onClick={() => setScale(Math.max(scale * 0.9, 0.1))}
          >
            <span className="is-icon-4 icon-minus icon-symbol"></span>
          </button>
          {props.onHide && (
            <button
              className="bar-icon"
              data-element-focusable="true"
              title="Close"
              onClick={() => props.onHide()}
            >
              <span className="is-icon-4 icon-close-icon-white-small icon-symbol"></span>
            </button>
          )}
        </div>
      </div>
    );
  }
  function updateFocusedPageFromScroll() {
    if (numPages == 1) {
      return;
    }
    const middle = window.innerHeight / 2;
    let left = 0;
    let right = numPages - 1;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const leftBoundingRect = pageRefs[left].current.getBoundingClientRect();
      const rightBoundingRect = pageRefs[right].current.getBoundingClientRect();

      const leftPagePos = (leftBoundingRect.top + leftBoundingRect.bottom) / 2;
      const rightPagePos = (rightBoundingRect.top + rightBoundingRect.bottom) / 2;

      const leftDist = middle - leftPagePos;
      const rightDist = rightPagePos - middle;

      if (right - left <= 1) {
        if (leftDist < rightDist) {
          setEditingPageNumber(left + 1);
          setFocusPageNumber(left + 1);
        } else {
          setEditingPageNumber(right + 1);
          setFocusPageNumber(right + 1);
        }
        break;
      }

      if (leftDist > rightDist) {
        left = Math.floor((right + left) / 2);
      } else {
        right = Math.floor((right + left) / 2);
      }
    }
  }

  return (
    <div className={`PDFViewer ${props.show ? 'is-active' : ''}`}>
      <div className="PDFcontent">
        {documentHeader()}
        <div
          className="PDFdocument"
          onScrollCapture={() => {
            if (!loading) {
              updateFocusedPageFromScroll();
            }
          }}
        >
          <Document file={props.file} onLoadSuccess={onDocumentLoadSuccess}>
            {!loading &&
              Array.from({ length: numPages }, (_, i) => i + 1).map((val) => {
                return (
                  <div key={val} ref={pageRefs[val - 1]} className="PDFPage">
                    <Page width={window.innerWidth * scale} pageNumber={val} />;
                  </div>
                );
              })}
          </Document>
        </div>
      </div>
    </div>
  );
}
